import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Mock search results for now
const MOCK_PEOPLE = [
  { id: "p1", name: "John Smith", birth: "1945", treeId: "tree-1" },
  { id: "p2", name: "Jane Smith", birth: "1948", treeId: "tree-1" },
  { id: "p3", name: "Robert Smith", birth: "1970", treeId: "tree-1" },
  { id: "p4", name: "Mary Johnson", birth: "1972", treeId: "tree-1" },
  { id: "p5", name: "Emily Smith", birth: "1995", treeId: "tree-1" },
];

export default function SearchBar() {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const results = query.length >= 2
    ? MOCK_PEOPLE.filter((p) =>
        p.name.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const showDropdown = focused && results.length > 0;

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-warmgray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder={`${t('common.search')} (Ctrl+K)`}
          className="w-64 rounded-lg border border-warmgray-200 bg-warmgray-50 py-2 pl-9 pr-8 text-sm text-warmgray-700 placeholder-warmgray-400 transition-all focus:w-80 focus:border-bark-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-bark-300"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-warmgray-400 hover:text-warmgray-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 z-20 mt-1 w-80 rounded-lg border border-warmgray-200 bg-white py-1 shadow-lg">
          {results.map((person) => (
            <button
              key={person.id}
              onMouseDown={() => {
                navigate(
                  `/tree/tree-1/person/${person.id}`
                );
                setQuery("");
              }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-bark-50"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-warmgray-100 text-xs font-medium text-warmgray-600">
                {person.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>
              <div>
                <div className="text-sm font-medium text-warmgray-800">
                  {person.name}
                </div>
                <div className="text-xs text-warmgray-500">
                  b. {person.birth}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
